import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getMyProfile, updateMyProfile } from "@/api/profileApi";
import { useSupabaseSession } from "@/hooks/useSupabaseSession";
import { useTheme } from "@/hooks/useTheme";

const UserManagementPage: React.FC = () => {
	useTheme();

	const { session, isLoading } = useSupabaseSession();
	const queryClient = useQueryClient();
	const [name, setName] = useState("");
	const [message, setMessage] = useState<{
		type: "success" | "error";
		text: string;
	} | null>(null);

	const user = session?.user;
	const identityProvider = user?.app_metadata?.provider as string | undefined;

	const { data: myProfile, isLoading: isProfileLoading } = useQuery({
		queryKey: ["profile", "me"],
		queryFn: ({ signal }) => getMyProfile({ signal }),
		enabled: !!session,
		retry: false,
	});

	useEffect(() => {
		if (!myProfile) return;
		setName(myProfile.profile.name ?? "");
	}, [myProfile]);

	const updateMutation = useMutation({
		mutationFn: (body: Parameters<typeof updateMyProfile>[0]) => updateMyProfile(body),
		onSuccess: async () => {
			setMessage({ type: "success", text: "Profile updated successfully." });
			await queryClient.invalidateQueries({ queryKey: ["profile", "me"] });
		},
		onError: (error) => {
			setMessage({
				type: "error",
				text: error instanceof Error ? error.message : "Failed to update profile",
			});
		},
	});

	const displayName = useMemo(() => {
		if (!user) return "";
		return user.user_metadata?.full_name || user.user_metadata?.name || user.email || "User";
	}, [user]);

	if (isLoading || isProfileLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center surface-alt">
				<div className="animate-pulse flex flex-col items-center">
					<div className="h-8 w-8 surface-muted rounded-full mb-4" />
					<div className="h-4 w-32 surface-muted rounded" />
				</div>
			</div>
		);
	}

	if (!session) {
		const next = encodeURIComponent(window.location.pathname + window.location.search);
		window.location.href = `/auth?next=${next}`;
		return null;
	}

	return (
		<div className="min-h-screen surface-alt py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
			<div className="max-w-xl mx-auto space-y-8">
				{/* Header Section */}
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-bold text-primary tracking-tight">Account Settings</h1>
						<p className="mt-1 text-sm text-subtle">Manage your personal information and session.</p>
					</div>
					<Link to="/app" className="text-sm font-medium text-muted hover:text-primary transition-colors">
						Back to Dashboard
					</Link>
				</div>

				{/* Main Profile Card */}
				<div className="card overflow-hidden border border-subtle shadow-sm">
					{/* Identity Header */}
					<div className="surface-alt p-6 border-b border-subtle flex items-center gap-5">
						<div className="h-16 w-16 rounded-full surface-muted flex items-center justify-center text-muted text-xl font-semibold border border-strong shadow-sm">
							{displayName.charAt(0).toUpperCase()}
						</div>
						<div className="flex-1 min-w-0">
							<h2 className="text-lg font-semibold text-primary truncate">{displayName}</h2>
							<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-subtle mt-0.5">
								<span className="truncate">{session.user.email}</span>
								{identityProvider && (
									<>
										<span className="text-subtle">•</span>
										<span className="capitalize inline-flex items-center gap-1">{identityProvider}</span>
									</>
								)}
							</div>
						</div>
					</div>

					<div className="p-6 space-y-6 surface">
						{/* Messages */}
						{message && (
							<div
								className={`p-4 rounded-lg text-sm flex items-start gap-2 ${
									message.type === "success"
										? "bg-green-50 text-green-700 border border-green-200"
										: "bg-red-50 text-red-700 border border-red-200"
								}`}
							>
								<span className="mt-0.5 text-base">{message.type === "success" ? "✓" : "!"}</span>
								{message.text}
							</div>
						)}

						{/* Edit Form */}
						<form
							className="space-y-6"
							onSubmit={(e) => {
								e.preventDefault();
								setMessage(null);
								updateMutation.mutate({ name });
							}}
						>
							<div className="space-y-1.5">
								<label htmlFor="name" className="block text-sm font-medium text-muted">
									Name
								</label>
								<input
									id="name"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									className="input transition-all duration-200 focus:ring-offset-0"
									autoComplete="name"
									placeholder="e.g. Jane Doe"
								/>
							</div>

							<div className="pt-2 flex justify-end">
								<button
									type="submit"
									disabled={updateMutation.isPending}
									className="btn btn-primary min-w-[100px] shadow-sm active:scale-95 transition-all duration-150"
								>
									{updateMutation.isPending ? "Saving..." : "Save Changes"}
								</button>
							</div>
						</form>
					</div>

					{/* Footer / Actions */}
					<div className="surface-alt px-6 py-4 border-t border-subtle flex items-center justify-between">
						<span className="text-xs text-subtle">
							User ID: <code className="font-mono">{session.user.id.slice(0, 8)}...</code>
						</span>
						<Link to="/logout" className="btn btn-ghost text-red-600 hover:text-red-700 hover:bg-red-50 text-sm h-9">
							Sign Out
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UserManagementPage;
